import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QuestionsTable } from '@/components/admin/questions/QuestionsTable';
import { CategoriesTab } from '@/components/admin/questions/CategoriesTab';
import { ImportTab } from '@/components/admin/questions/ImportTab';
import { FlaggedQuestionsTab } from '@/components/admin/questions/FlaggedQuestionsTab';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useCategories } from '@/hooks/useCategories';
import { useQuestions } from '@/hooks/useQuestions';
import { AddQuestionDialog } from '@/components/admin/questions/AddQuestionDialog';
import { EditQuestionDialog } from '@/components/admin/questions/EditQuestionDialog';
import { ViewQuestionDialog } from '@/components/admin/questions/ViewQuestionDialog';
import { DeleteConfirmDialog } from '@/components/admin/questions/DeleteConfirmDialog';
import { CategoryDialog } from '@/components/admin/questions/CategoryDialog';
import { ImportDialog } from '@/components/admin/questions/ImportDialog';
import { Question, Category, NewCategory, NewQuestion } from '@/components/admin/questions/types';

const QuestionManagement = () => {
  const { toast } = useToast();
  const { categories, loading: categoriesLoading, createCategory, updateCategory, deleteCategory } = useCategories();
  const {
    paginatedQuestions: questions,
    loading: questionsLoading,
    searchTerm,
    setSearchTerm,
    typeFilter,
    setTypeFilter,
    currentPage,
    setCurrentPage,
    totalQuestions,
    questionsPerPage,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    bulkUpdateQuestions,
    uploadFile,
    bulkDeleteQuestions
  } = useQuestions();

  const [activeTab, setActiveTab] = useState('questions');
  
  // Dialog states
  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setViewDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [isImportDialogOpen, setImportDialogOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Data for dialogs
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; type: 'question' | 'category' } | null>(null);

  // Handlers for opening dialogs
  const handleOpenAddDialog = () => setAddDialogOpen(true);

  const handleOpenEditDialog = (question: Question) => {
    setSelectedQuestion(question);
    setEditDialogOpen(true);
  };

  const handleOpenViewDialog = (question: Question) => {
    setSelectedQuestion(question);
    setViewDialogOpen(true);
  };

  const handleOpenDeleteDialog = (id: string, type: 'question' | 'category') => {
    setDeleteTarget({ id, type });
    setDeleteConfirmOpen(true);
  };

  const handleOpenCreateCategoryDialog = () => {
    setSelectedCategory(null);
    setCategoryDialogOpen(true);
  };

  const handleOpenEditCategoryDialog = (category: Category) => {
    setSelectedCategory(category);
    setCategoryDialogOpen(true);
  };

  // CRUD Handlers
  const handleAddQuestion = async (data: NewQuestion) => {
    await addQuestion(data);
    toast({ title: "Success", description: "Question added successfully." });
    setAddDialogOpen(false);
  };

  const handleEditQuestion = async (data: Partial<Question>) => {
    if (!selectedQuestion) return;

    try {
      // Only send editable fields to avoid overwriting server-managed fields
      const processedData: Partial<NewQuestion> = {
        question_text: data.question_text,
        question_type: data.question_type,
        options: data.options,
        correct_answer: Array.isArray(data.correct_answer)
          ? data.correct_answer
          : data.correct_answer
            ? [data.correct_answer]
            : [],
        explanation: data.explanation,
        category_id: data.category_id,
        question_images: data.question_images,
        explanation_images: data.explanation_images,
      };

      await updateQuestion(selectedQuestion.id, processedData);
      toast({ title: "Success", description: "Question updated successfully." });
      setEditDialogOpen(false);
      setSelectedQuestion(null);
    } catch (error) {
      console.error('Error updating question:', error);
      toast({ 
        title: "Error", 
        description: "Failed to update question. Please try again.",
        variant: "destructive" 
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'question') {
      await deleteQuestion(deleteTarget.id);
      toast({ title: "Success", description: "Question deleted successfully." });
    } else {
      await deleteCategory(deleteTarget.id);
      toast({ title: "Success", description: "Category deleted successfully." });
    }
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const handleCreateCategory = async (data: NewCategory) => {
    await createCategory(data);
    toast({ title: "Success", description: "Category created successfully." });
    setCategoryDialogOpen(false);
  };

  const handleUpdateCategory = async (data: Partial<Category>) => {
    if (!data.id) return;
    await updateCategory(data.id, { name: data.name, description: data.description });
    toast({ title: "Success", description: "Category updated successfully." });
    setCategoryDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleBulkAction = async (action: string, selectedIds: string[], value?: string) => {
    if (selectedIds.length === 0) {
      toast({ title: 'No questions selected', description: 'Please select at least one question to perform a bulk action.', variant: 'destructive' });
      return;
    }

    if (action === 'delete') {
      await bulkDeleteQuestions(selectedIds);
    } else if (action === 'hide') {
      await bulkUpdateQuestions(selectedIds, { is_active: false } as Partial<Question>);
      toast({ title: 'Success', description: `${selectedIds.length} question(s) hidden from users.` });
    } else if (action === 'show') {
      await bulkUpdateQuestions(selectedIds, { is_active: true } as Partial<Question>);
      toast({ title: 'Success', description: `${selectedIds.length} question(s) made visible to users.` });
    } else if (action === 'assign_category') {
      await bulkUpdateQuestions(selectedIds, { category_id: value || null } as Partial<Question>);
      toast({ title: 'Success', description: `Category assigned to ${selectedIds.length} question(s).` });
    }
  };

  const handleToggleVisibility = async (question: Question) => {
    try {
      await updateQuestion(question.id, { is_active: !question.is_active });
      toast({
        title: 'Success',
        description: `Question ${question.is_active ? 'hidden from' : 'made visible to'} users.`
      });
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast({ title: 'Error', description: 'Failed to update question visibility.', variant: 'destructive' });
    }
  };

  const handleImport = async (file: File, categoryId?: string, university?: string) => {
    await uploadFile(file, categoryId, university);
    setImportDialogOpen(false);
  };

  const isLoading = questionsLoading || categoriesLoading;

  return (
    <DashboardLayout isAdmin>
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Question Management</h1>
          <p className="text-muted-foreground">
            Manage questions, categories, and review flagged content
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="questions">Questions</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="import">Import</TabsTrigger>
            <TabsTrigger value="flagged">Flagged</TabsTrigger>
          </TabsList>

          <TabsContent value="questions" className="space-y-4">
            <QuestionsTable 
              questions={questions}
              loading={questionsLoading}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalQuestions={totalQuestions}
              questionsPerPage={questionsPerPage}
              onImportClick={() => setImportDialogOpen(true)}
              onDeleteClick={(id: string) => {
                setDeleteTarget({ id, type: 'question' });
                setDeleteConfirmOpen(true);
              }}
              onEditClick={(question) => {
                setSelectedQuestion(question);
                setEditDialogOpen(true);
              }}
              onViewClick={(question) => {
                setSelectedQuestion(question);
                setViewDialogOpen(true);
              }}
              onBulkAction={handleBulkAction}
              onToggleVisibility={handleToggleVisibility}
            />
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <CategoriesTab 
              categories={categories}
              loading={categoriesLoading}
              onCreateCategory={handleOpenCreateCategoryDialog}
              onDeleteCategory={(id: string) => {
                setDeleteTarget({ id, type: 'category' });
                setDeleteConfirmOpen(true);
              }}
              onEditCategory={(category) => {
                setSelectedCategory(category);
                setCategoryDialogOpen(true);
              }}
            />
          </TabsContent>

          <TabsContent value="import" className="space-y-4">
            <ImportTab onImportClick={() => setImportDialogOpen(true)} uploading={false} />
          </TabsContent>

          <TabsContent value="flagged" className="mt-4">
            <FlaggedQuestionsTab />
          </TabsContent>
        </Tabs>
      </div>

      <AddQuestionDialog
        isOpen={isAddDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAddQuestion={handleAddQuestion}
        categories={categories}
      />

      {selectedQuestion && (
        <EditQuestionDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedQuestion(null);
          }}
          onEditQuestion={handleEditQuestion}
          question={selectedQuestion}
          categories={categories}
        />
      )}

      {selectedQuestion && (
        <ViewQuestionDialog
          isOpen={isViewDialogOpen}
          onClose={() => {
            setViewDialogOpen(false);
            setSelectedQuestion(null);
          }}
          question={selectedQuestion}
        />
      )}

      <DeleteConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName={deleteTarget?.type === 'question' ? 'question' : 'category'}
      />

      <CategoryDialog
        isOpen={isCategoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          setSelectedCategory(null);
        }}
        onCreate={handleCreateCategory}
        onUpdate={handleUpdateCategory}
        category={selectedCategory}
      />

      <ImportDialog
        isOpen={isImportDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
        uploading={questionsLoading} // Or a dedicated uploading state
        categories={categories}
      />
    </DashboardLayout>
  );
};

export default QuestionManagement;
